import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ADMIN_LOGIN_URL } from "@/constants/adminRoutes";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "sarathix_admin_token";
const STORAGE = sessionStorage;
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Set default auth header
function setAxiosToken(token) {
  if (token) {
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common["Authorization"];
  }
}

// ── Global 401 handler for admin API calls ──────────────────────────────
// Admin access tokens expire after 30 min. Without this, every admin
// action after expiry surfaces a generic "Failed to X" toast and the
// operator is stuck without knowing they need to log back in. The
// interceptor only fires for `/api/admin/*` and `/api/auth/me` so it
// never interferes with the customer portal's own 401 handling.
let _401HandlerInstalled = false;
function install401Interceptor() {
  if (_401HandlerInstalled) return;
  _401HandlerInstalled = true;
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status;
      const url = error?.config?.url || "";
      const isAdminCall = url.includes("/api/admin/") || url.endsWith("/api/auth/me");
      if (status === 401 && isAdminCall) {
        // Clear the dead token so AuthProvider's checkAuth doesn't loop.
        if (STORAGE.getItem(TOKEN_KEY)) {
          STORAGE.removeItem(TOKEN_KEY);
          setAxiosToken(null);
          toast.error("Session expired. Please log in again.");
        }
        // Only redirect if we're inside the admin panel — don't yank a
        // customer or homepage visitor to /caldera/.../login.
        const currentPath = window.location?.pathname || "";
        if (currentPath.startsWith("/caldera/") && !currentPath.endsWith("/login")) {
          window.location.replace(ADMIN_LOGIN_URL);
        }
      }
      return Promise.reject(error);
    }
  );
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Install the global 401 → admin-login redirect interceptor exactly once.
  useEffect(() => {
    install401Interceptor();
  }, []);

  const checkAuth = useCallback(async () => {
    const token = STORAGE.getItem(TOKEN_KEY);
    if (!token) { setUser(false); setLoading(false); return; }
    setAxiosToken(token);
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
    } catch {
      STORAGE.removeItem(TOKEN_KEY);
      setAxiosToken(null);
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []); // API, STORAGE, TOKEN_KEY are module-level constants — stable

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = useCallback(async (username, password, totpCode) => {
    const payload = { username, password };
    if (totpCode) payload.totp_code = totpCode;
    const res = await axios.post(`${API}/auth/login`, payload);
    if (res.data.requires_totp) return res.data;
    STORAGE.setItem(TOKEN_KEY, res.data.token);
    setAxiosToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  }, []);

  const logout = useCallback(async () => {
    // FIX-9 — call the server first so the access-token's jti is added to
    // `revoked_tokens` before we drop our local copy. Errors are swallowed
    // (logout must be idempotent and resilient to network failures).
    try { await axios.post(`${API}/auth/logout`); } catch (_) { /* idempotent */ }
    STORAGE.removeItem(TOKEN_KEY);
    setAxiosToken(null);
    setUser(false);
  }, []);

  const value = useMemo(() => ({ user, loading, login, logout }), [user, loading, login, logout]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
